// This file follows the pattern of useRangeSliderFilterConfig.ts, but is simplified since the unit does not change for metric users.
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { getUnitAbbreviation } from '@alltrails/data-formatting/utils';
import { RangeSliderFilterConfig } from '@alltrails/denali/components/FilterBar';
import ExploreServiceFilters from '../../types/ExploreServiceFilters';

const unit = 'hour' as const;
const values = [
  ...Array.from(Array(9).keys()).map(v => v * 0.5), // 0-4, in 0.5 increments
  ...Array.from(Array(4).keys()).map(v => v + 5) // 5-8, in 1 increments
];

const minutesToHours = (minutes: number) => Math.round(minutes / 30) / 2; // Nearest half hour
const hoursToMinutes = (hours: number) => hours * 60;

export const getValidDuration = (minutes: number) => values.map(hoursToMinutes).find(v => minutes <= v);

const useDurationFilterConfig = (
  exploreServiceFilters: ExploreServiceFilters,
  onFiltersChange: (updatedFilters: ExploreServiceFilters) => void
): RangeSliderFilterConfig => {
  const intl = useIntl();
  const minValue = values[0];
  const maxValue = values[values.length - 1];

  const lowerValue = useMemo(() => {
    if (exploreServiceFilters.duration?.min !== undefined) {
      return minutesToHours(exploreServiceFilters.duration.min);
    }
    return minValue;
  }, [exploreServiceFilters, minValue]);

  const upperValue = useMemo(() => {
    if (exploreServiceFilters.duration?.max !== undefined) {
      return minutesToHours(exploreServiceFilters.duration.max);
    }
    return maxValue;
  }, [exploreServiceFilters, maxValue]);

  const marks = useMemo(
    () =>
      values.map((value, index) => {
        let label: string | undefined = undefined;
        if (index === 0) {
          label = intl.formatNumber(value, { style: 'unit', unit });
        } else if (index === values.length - 1) {
          label = `${intl.formatNumber(value, { style: 'unit', unit })}+`;
        }
        return { value, label };
      }),
    [intl]
  );

  const formatValues = useCallback(
    (v1: number, v2: number) => {
      if (v1 === minValue && v2 === maxValue) {
        return intl.formatMessage({ defaultMessage: 'Any' });
      } else if (v2 === maxValue) {
        return `${intl.formatNumber(v1, { style: 'unit', unit })}+`;
      }
      const showPlus = v2 === maxValue;
      return `${intl.formatNumber(v1)}–${intl.formatNumber(v2)} ${getUnitAbbreviation(intl, unit)}${showPlus ? '+' : ''}`;
    },
    [intl, maxValue, minValue]
  );

  const onChange = useCallback(
    (values?: [number, number]) => {
      const newFilters = { ...exploreServiceFilters };
      delete newFilters.duration;
      if (values) {
        const [v1, v2] = values;
        if (v1 === v2) {
          // Don't let users select no range since this is extremely likely to return no results
          return;
        }
        if (v1 === minValue && v2 === maxValue) {
          // Do nothing, act like there is no duration filter
        } else if (v1 === minValue) {
          newFilters.duration = { max: hoursToMinutes(v2) };
        } else if (v2 === maxValue) {
          newFilters.duration = { min: hoursToMinutes(v1) };
        } else {
          newFilters.duration = { min: hoursToMinutes(v1), max: hoursToMinutes(v2) };
        }
      }
      onFiltersChange(newFilters);
    },
    [exploreServiceFilters, maxValue, minValue, onFiltersChange]
  );

  const isDefault = lowerValue === minValue && upperValue === maxValue;

  const clear = useMemo(() => {
    if (isDefault) {
      return undefined;
    }
    return () => onChange(undefined);
  }, [isDefault, onChange]);

  return {
    type: 'range-slider',
    id: 'duration',
    filterChip: {
      onClear: clear,
      selectedItems: isDefault ? undefined : [formatValues(lowerValue, upperValue)],
      testId: 'duration-filter-chip',
      unselectedText: intl.formatMessage({ defaultMessage: 'Estimated time' })
    },
    rangeSlider: {
      formatHeaderValue: ([v1, v2]) => formatValues(v1, v2),
      label: intl.formatMessage({ defaultMessage: 'Estimated time' }),
      marks,
      onChange,
      testId: 'duration-filter',
      value: [lowerValue, upperValue]
    }
  };
};

export default useDurationFilterConfig;
